/**
 * Debounced search input
 * Story 198: View and filter stories in backlog
 */

import { useState, useEffect, useCallback } from 'react';
import './SearchBox.css';

/** Debounce delay in milliseconds */
const DEBOUNCE_DELAY_MS = 300;

interface SearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function SearchBox({ value, onChange, placeholder = 'Search...' }: SearchBoxProps) {
  const [localValue, setLocalValue] = useState(value);

  // Sync local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, DEBOUNCE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [localValue, value, onChange]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  return (
    <div className="search-box">
      <svg className="search-box__icon" viewBox="0 0 16 16" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M11.5 7a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0zm-.82 4.74a6 6 0 1 1 1.06-1.06l3.04 3.04a.75.75 0 1 1-1.06 1.06l-3.04-3.04z"
        />
      </svg>
      <input
        type="text"
        className="search-box__input"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        autoFocus
      />
      {localValue && (
        <button
          className="search-box__clear"
          onClick={handleClear}
          type="button"
          aria-label="Clear search"
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06z"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
